import React, { useState, useEffect, useContext } from "react";
import { GlobalContext } from "./GlobalContext";
import AddEditBroker from "./AddEditBroker";
import CommonDataView from "./CommonDataView";
import SheetFooter from "./components/SheetFooter";
import useTabularDataManager from "./useTabularDataManager";
import useFieldOrder from "./useFieldOrder";
import useRowSelection from "./hooks/useRowSelection";
import FloatingDownloadButton from "./FloatingDownloadButton";
import JoyridePortal from "./JoyridePortal";
import CustomTooltip from "./JoyrideCustomTooltip";
import useCrmSheetTour from "./useCrmSheetTour";
import useTourRegistration from "./hooks/useTourRegistration";
import { themeAccent } from "./theme";
import { emitter } from "./UtilityFunctions";

function Brokers({ brokers=[], setBrokers, pagination, setPagination , isLoading = false }){
    const ownSheetName = "Brokers";
    const { can } = useContext(GlobalContext);
    const {
        modalVisible, setModalVisibility,
        messagesModalVisible, setMessagesModalVisible,
        sortOrder, sortData,
        filterObject, filterData,
        filteredDataArray
        } = useTabularDataManager(brokers, setBrokers);

    //Lives here rather than in Content: the checkboxes are rendered by the data
    //view and the resulting action sits in the footer, and those are siblings.
    const selection = useRowSelection();

    //Hand the picked ids to the broadcast composer. Same emitter the
    //notification deep-links use (CarsNav runs the unsaved-changes guard on it,
    //SubNav picks the payload up) — no second navigation mechanism.
    const messageSelected = (ids) => {
        emitter.emit("navigate", {
            tab: "tab-messages",
            subMenu: "Compose",
            broadcastSelection: { type: "broker", ids: [...ids] },
        });
    };

    //Pagination changes refetch automatically: the useSheetData query key in
    //SubNav includes currentPage/recordsPerPage/searchTerm

    //One tour shared with the other two contact sheets — the three screens are
    //the same components with a different noun. No `enabled` gate on the
    //registration: unlike SubNav, this component only mounts on its own tab.
    const canMessage = can('messages.broadcast');
    const { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback } =
        useCrmSheetTour({ noun: "brokers", label: "Brokers", contentId: "brokers-content", canMessage });
    useTourRegistration('brokers', handleStartTutorial);

    return (
        <div id="brokers-content" className="flex flex-col h-full section-viewport">
            <JoyridePortal
                steps={steps}
                stepIndex={stepIndex}
                run={runTour}
                callback={handleJoyrideCallback}
                continuous
                showProgress
                showSkipButton
                disableCloseOnEsc
                disableOverlayClose
                spotlightPadding={3}
                locale={{ back: 'Back', close: 'Close', last: 'Finish', next: 'Next', skip: 'Skip' }}
                styles={{ options: { primaryColor: themeAccent(), zIndex: 10000 } }}
                tooltipComponent={CustomTooltip}
            />
            <Content isLoading={isLoading} 
                modalVisible={modalVisible} 
                setModalVisibility={setModalVisibility} 
                filteredDataArray={filteredDataArray} 
                sortData={sortData}  
                sortOrder={sortOrder}
                parentSheetName={ownSheetName}
                filterData={filterData}
                filterObject={filterObject}
                ownSheetName={ownSheetName}
                pagination={pagination}
                setPagination={setPagination}
                selection={selection}
            />
            <Summary
                setModalVisibility={setModalVisibility}
                pagination={pagination}
                setPagination={setPagination}
                selectionBar={{
                    selection,
                    onMessage: messageSelected,
                    canMessage,
                    moreThanPage: (pagination?.totalPages || 1) > 1,
                }}
            />
        </div>
    )
}

function Content({ filteredDataArray, modalVisible, setModalVisibility, sortData, sortOrder, parentSheetName, filterData, filterObject, ownSheetName, pagination, setPagination, selection, isLoading }){
    const [fieldOrder, setFieldOrder] = useFieldOrder(parentSheetName, filteredDataArray[0], ['Name', 'Phone1', 'Address']);
    const [ currentBrokerIndex, setCurrentBroker ] = useState();
    const openModalEdit = (brokerId)=>{
        setCurrentBroker(brokerId);
        setModalVisibility({
            visible: true,
            mode: "edit"
        });
    }

    const [ selectedRow, setSelectedRow ] = useState("")

    const selectRow = (rowIndex)=>{
        setSelectedRow(rowIndex)
    }

    return (
        <div className="flex-grow overflow-auto min-h-0">
            <CommonDataView
                isLoading={isLoading}
                objectsArray={filteredDataArray}
                sortData={sortData}
                sortOrder={sortOrder}
                fieldOrder={fieldOrder}
                setFieldOrder={setFieldOrder}
                parentSheetName={parentSheetName}
                filterData={filterData}
                filterObject={filterObject}
                sheetName={ownSheetName}
                selectRow={selectRow}
                selectedRow={selectedRow}
                openModalEdit={openModalEdit}
                primaryKeyName={"BrokerID"}
                pagination={pagination}
                setPagination={setPagination}
                searchPlaceholder="Broker name"
                selection={selection}
            />
            
            <FloatingDownloadButton
                data={filteredDataArray}
                fieldOrder={fieldOrder}
                sheetName={ownSheetName}
                pagination={pagination}
                filterObject={filterObject}
            />


            {
                modalVisible.visible &&  <AddEditBroker 
                formMode={modalVisible.mode} 
                showModal={setModalVisibility} 
                brokerData={filteredDataArray[currentBrokerIndex]} 
                BrokerID={filteredDataArray[currentBrokerIndex]?.BrokerID}
                parentSheetName={parentSheetName} />
            }

        </div>
    )
}

function Summary({ setModalVisibility, pagination, setPagination, selectionBar }){
    const { can } = useContext(GlobalContext);
    return (
        <SheetFooter
            countLabel="Brokers Displayed:"
            searchPlaceholder="Search brokers…"
            pagination={pagination}
            setPagination={setPagination}
            canAdd={can('crm.edit')}
            onAdd={() => setModalVisibility({ visible: true, mode: "add" })}
            selectionBar={selectionBar}
        />
    );
}

export default Brokers;